// VH-226 — pure helpers for the AI plan generator and review.

import { getWeekNumber } from '../workouts/workoutHelpers'

export const GOAL_OPTIONS = ['build_muscle', 'strength', 'lose_weight', 'improve_endurance']
export const LEVEL_OPTIONS = ['beginner', 'intermediate', 'advanced']

/** ISO year + week of the week after `today` — the default block start. */
export function nextIsoWeek(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7)
  const week = getWeekNumber(d)
  // The ISO year is the year of that week's Thursday.
  const thursday = new Date(d)
  thursday.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7))
  return { year: thursday.getFullYear(), week }
}

export function buildGenerateRequest(options) {
  return {
    fitness_goals: options.fitness_goals,
    fitness_level: options.fitness_level,
    days_per_week: Number(options.days_per_week),
    session_duration_minutes: Number(options.session_duration_minutes),
    injuries_limitations: options.injuries_limitations?.trim() || null,
    use_analysis: Boolean(options.use_analysis),
    block_weeks: Number(options.block_weeks),
    auto_create_program: false,
  }
}

/**
 * The weeks to review. A plain generation (no block) returns only
 * weekly_schedule; wrap it as a single week so review and accept work the same.
 */
export function planWeeks(plan) {
  if (plan?.weeks?.length) return plan.weeks
  if (plan?.weekly_schedule?.length) {
    return [{ week_number: 1, is_deload: false, days: plan.weekly_schedule }]
  }
  return []
}

export function exerciseName(exercise, language = 'en') {
  return language?.startsWith('de') ? exercise.exercise_name_de : exercise.exercise_name_en
}

export function removeExercise(weeks, weekNumber, dayNumber, index) {
  return weeks.map((w) =>
    w.week_number !== weekNumber
      ? w
      : {
          ...w,
          days: w.days.map((d) =>
            d.day_number !== dayNumber
              ? d
              : { ...d, exercises: d.exercises.filter((_, i) => i !== index) }
          ),
        }
  )
}

export function removeSession(weeks, weekNumber, dayNumber) {
  return weeks.map((w) =>
    w.week_number !== weekNumber ? w : { ...w, days: w.days.filter((d) => d.day_number !== dayNumber) }
  )
}

/** Reasons naming an exercise no longer anywhere in the plan are dropped. */
export function activeReasons(reasons = [], weeks = []) {
  const names = new Set()
  weeks.forEach((w) =>
    w.days.forEach((d) =>
      d.exercises.forEach((e) => {
        names.add(e.exercise_name_en?.toLowerCase())
        names.add(e.exercise_name_de?.toLowerCase())
      })
    )
  )
  return reasons.filter((r) => !r.exercise_name || names.has(r.exercise_name.toLowerCase()))
}

/** Exercises the backend would reject: not matched to a library exercise. */
export function unmatchedExercises(weeks = []) {
  const out = []
  weeks.forEach((w) =>
    w.days.forEach((d) =>
      d.exercises.forEach((e, index) => {
        if (!e.exercise_definition_id) out.push({ week: w.week_number, day: d.day_number, index, exercise: e })
      })
    )
  )
  return out
}

export function buildAcceptRequest(weeks, start) {
  return {
    weeks: weeks.filter((w) => w.days.length > 0),
    start_year: Number(start.year),
    start_week: Number(start.week),
  }
}

/** Normalises an accept failure into { kind, conflicts, problems, message }. */
export function parseAcceptError(error) {
  const status = error?.response?.status
  const detail = error?.response?.data?.detail
  if (status === 409) {
    return { kind: 'conflict', conflicts: detail?.conflicts ?? [], problems: [] }
  }
  if (status === 422) {
    const problems = Array.isArray(detail?.problems)
      ? detail.problems
      : Array.isArray(detail)
        ? detail.map((d) => d.msg).filter(Boolean)
        : []
    return { kind: 'invalid', conflicts: [], problems, message: detail?.message }
  }
  return { kind: 'error', conflicts: [], problems: [] }
}
